import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, throwError, TimeoutError } from 'rxjs';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SettingsService } from '../../services/settings.service';
import { UserSettingsService } from '../../services/user-settings.service';
import { SnackbarService } from '../../services/snackbar.sevice';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { Constants } from '../../models/constants';
import { CountrySetting } from '../../models/countrysetting';
import { requireCheckboxesToBeCheckedValidator } from '../create-user/create-user.component';
import { PhoneNumberUtil, PhoneNumber } from 'google-libphonenumber';
import { HttpErrorResponse } from '@angular/common/http';
import { SitePermissionInfo, Title } from '../my-hospital-user.model';
import { SpinnerService } from '../../services/spinner.service';
import { Org, User } from '../../models/user';
import {
    RoleEnum,
    UserDetail,
    PatchUserDetails,
    UpdateUserOrgRole,
    UpdateUserRolesRequest,
    Role,
    PatchUser
} from 'src/app/api-module/user-management-api-client-v2';
import { RoleMappingService } from 'src/app/services/role-mapping.service';
import { OrgSiteSelectionService } from 'src/app/shared/org-site-selection/org-site-selection.service';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { SettingUtilities } from '@app/shared/setting-utilities';
import { SettingType } from '@app/api-module/control-centre-api-client';

const phoneUtil = PhoneNumberUtil.getInstance();

@Component({
    selector: 'app-view-user',
    templateUrl: './view-user.component.html',
    styleUrls: ['./view-user.component.scss']
})
export class ViewUserComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly UserMfaResetMessage = "The user's authentication has successfully been reset.";
    private readonly UserMfaResetErrorMessage =
        'There was an issue resetting MFA for this user. Please try again, and if it recurs contact the support team.';
    private readonly NamePattern = "[a-zA-ZÀ-ž\u0370-\u03FF\u0400-\u04FF][0-9a-zA-ZÀ-ž\u0370-\u03FF\u0400-\u04FF,.@'-_^ ]*";

    // Phone number related constants
    private readonly PhonePattern = '[0-9]*';
    private readonly MaxSizePhoneExtension = 10;

    private readonly subscriptions = new Subscription();
    readonly viewingUserBehaviorSubject = new BehaviorSubject<UserDetail>(null);

    @ViewChild('viewUserContainer') viewUserContainer: ElementRef;

    readonly id: string;
    readonly roles: Role[];
    roleEnum = RoleEnum;
    userDetailsFormGroup: UntypedFormGroup;
    rolesFormGroup: UntypedFormGroup;
    defaultSiteFormControl: UntypedFormControl;
    defaultCountryCode$: Observable<string>;
    countryCode$ = new BehaviorSubject<string>(null);
    countryCode: string;
    userHasMfa$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    mfaResetting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly orgSelected = new BehaviorSubject<Org>(null);
    sitesSource$ = this.orgSiteSelectionService.getAllSites();
    private readonly permittedSites = new BehaviorSubject<number[]>([]);
    permittedSites$ = this.permittedSites.asObservable();
    currentDefaultSite$: Observable<number>;
    triggerSitesRefresh$ = new EventEmitter<void>();
    updatedSitePermission = new BehaviorSubject<SitePermissionInfo[]>([]);
    numSiteSelected$: Observable<number>;
    private isSitePermissionChanged = false;
    roleMapping = RoleMappingService;
    saving = false;
    loading = true;
    loggedInUser: User;

    readonly titles: Title[] = [
        { value: 'Dr.', viewValue: 'Dr.' },
        { value: 'Miss', viewValue: 'Miss' },
        { value: 'Ms.', viewValue: 'Ms.' },
        { value: 'Mrs.', viewValue: 'Mrs.' },
        { value: 'Mr.', viewValue: 'Mr.' }
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        public dialogRef: MatDialogRef<ViewUserComponent>,
        private readonly renderer: Renderer2,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly settingsService: SettingsService,
        private readonly userSettingsService: UserSettingsService,
        private readonly snackbarService: SnackbarService,
        private readonly spinnerService: SpinnerService,
        private readonly authenticationService: AuthenticationService
    ) {
        dialogRef.disableClose = true;
        this.id = data.id;
        this.roles = [...data.roles];
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get userDetailsForm() {
        return this.userDetailsFormGroup.controls;
    }

    get rolesForm() {
        return this.rolesFormGroup.controls;
    }

    ngOnInit(): void {
        this.loggedInUser = this.authenticationService.getCurrentUser();

        this.initForm();
        this.initCountryCodeAndSelectedOrg();
        this.initSitePermission();
        this.loadUser();
        this.spinnerService.show();
    }

    ngAfterViewInit(): void {
        const viewUserContainer = this.viewUserContainer.nativeElement as HTMLElement;
        this.renderer.setStyle(viewUserContainer, 'width', viewUserContainer.offsetWidth + 'px');
    }

    async submit(): Promise<void> {
        this.markAsTouched(this.userDetailsFormGroup);
        this.markAsTouched(this.rolesFormGroup);
        const requestList: Observable<any>[] = [];

        if (!this.userDetailsFormGroup.valid || !this.rolesFormGroup.valid || !this.isRoleAndSiteValid()) {
            this.snackbarService.openSnackBar(Constants.UserCannotBeUpdated, SnackbarService.SNACK_BAR_CLASS_ALERT);
            return;
        }

        if (this.userDetailsFormGroup.dirty) {
            const userDetails = this.getUserDetailsFromForm();

            requestList.push(this.userSettingsService.updateUserV2(this.id, userDetails).pipe(catchError(this.getErrorMessage)));
        }

        if (this.rolesFormGroup.dirty || this.isSitePermissionChanged) {
            const userRoles = this.getUserRolesFromForm();
            requestList.push(
                this.userSettingsService
                    .updateUserOrgRoles(this.id, this.orgSelected.value.orgCode, userRoles)
                    .pipe(catchError(this.getErrorMessage))
            );
        }

        if (this.defaultSiteFormControl.dirty && this.defaultSiteFormControl.value !== null) {
            const userDefaultSiteRequest = new PatchUserDetails({
                defaultSiteId: this.defaultSiteFormControl.value
            });

            requestList.push(
                this.userSettingsService
                    .updateUserDefaultSite(this.id, this.orgSelected.value.orgCode, userDefaultSiteRequest)
                    .pipe(catchError(this.getErrorMessage))
            );
        }

        if (requestList.length > 0) {
            this.saving = true;
            const userOrg = this.viewingUserBehaviorSubject.value.userOrgs.find(org => org.orgCode === this.orgSelected.value.orgCode);
            if (!userOrg) {
                try {
                    await this.userSettingsService
                        .createUserOrg(this.id, {
                            orgCode: this.orgSelected.value.orgCode,
                            defaultSiteId: this.defaultSiteFormControl.value === 0 ? null : this.defaultSiteFormControl.value
                        })
                        .toPromise();
                } catch (error) {
                    this.snackbarService.openSnackBar(error.message, SnackbarService.SNACK_BAR_CLASS_ALERT);
                    this.saving = false;
                }
            }

            let hasError;
            for (const req of requestList) {
                try {
                    await req.toPromise();
                } catch (error) {
                    hasError = error;
                }
            }
            this.saving = false;
            if (hasError) {
                this.snackbarService.openSnackBar(hasError, SnackbarService.SNACK_BAR_CLASS_ALERT);
            } else {
                if (this.loggedInUser.id === this.id) {
                    await this.authenticationService.notifyUserUpdated();
                }
                this.snackbarService.openSnackBar(Constants.UserSuccessfullyUpdated, SnackbarService.SNACK_BAR_CLASS_SUCCESS);
                this.dialogRef.close();
            }
        }
    }

    cancelWindow(): void {
        this.dialogRef.close();
    }

    getErrorMessage(response: HttpErrorResponse | TimeoutError): Observable<never> {
        if (response instanceof TimeoutError) {
            console.error('Request timeout: ', JSON.stringify(response));
            return throwError(Constants.UnableToContactServerMessage);
        } else if (
            response instanceof HttpErrorResponse &&
            (response.error instanceof ProgressEvent || response.status === 502 || response.status === 503 || response.status === 504)
        ) {
            console.error('Unable to contact server: ', response.status);
            return throwError(Constants.UnableToContactServerMessage);
        }
        console.error(`Server returned code ${response.status} with body:\n${JSON.stringify(response.error)}`);
        if (response.status === 403) {
            return throwError('You do not have permission to update this user');
        }
        if (response.status === 400) {
            // SPA shouldn't submit an invalid form. if it does and server returns error, it's an error scenario.
            const errorMessage =
                'Something went wrong while updating the user. Please try again in some time. ' +
                'If the problem persists, please contact Everlight support team';
            return throwError(errorMessage);
        }
        if (response.status === 404) {
            const errorMessage =
                'Cannot load the user details. It may have been deleted. Please try again in some time. ' +
                'If the problem persists, please contact Everlight support team';
            return throwError(errorMessage);
        }

        return throwError(
            'User update failed. Please try again in some time. ' + 'If the problem persists, please contact Everlight support team'
        );
    }

    private initForm() {
        this.userDetailsFormGroup = this.formBuilder.group({
            title: [null],
            firstName: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(this.NamePattern)]],
            lastName: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(this.NamePattern)]],
            emailAddress: [{ value: null, disabled: true }],
            contactPhone: [null, [Validators.pattern(this.PhonePattern), Validators.maxLength(15)]],
            contactPhoneExt: [null, [Validators.pattern(this.PhonePattern), Validators.maxLength(this.MaxSizePhoneExtension)]]
        });

        this.rolesFormGroup = new UntypedFormGroup({}, requireCheckboxesToBeCheckedValidator());
        this.roles.forEach(role => {
            this.rolesFormGroup.addControl(RoleMappingService.getRoleEnum(role.name), new UntypedFormControl(false));
        });

        this.defaultSiteFormControl = new UntypedFormControl(null);

        const phoneChange$ = combineLatest([this.userDetailsForm.contactPhone.valueChanges as Observable<string>, this.countryCode$]);

        this.subscriptions.add(
            phoneChange$.subscribe(([term, countryCode]) => {
                this.onPhoneNumberChanged(term, countryCode);
            })
        );
    }

    private initCountryCodeAndSelectedOrg() {
        // Pipe to get CountrySetting[] observable
        const countrySettings$ = this.settingsService
            .getCountrySettings()
            .pipe(map(response => response.map(item => new CountrySetting(item.country, item.region, item.countryCode))));

        // Pipe to update OrgSelected and return combined result of countryCode.
        this.defaultCountryCode$ = combineLatest([this.orgSiteSelectionService.getOrgSelected$(), countrySettings$]).pipe(
            map(([selectedOrg, countrySettings]) => {
                if (!selectedOrg) {
                    return '';
                }
                this.orgSelected.next(selectedOrg);
                return countrySettings.find(c => c.country === selectedOrg.countryCode)?.countryCode;
            })
        );
    }

    private initSitePermission() {
        this.subscriptions.add(
            combineLatest([
                this.rolesFormGroup.get(RoleEnum.GeneralAccess).valueChanges.pipe(startWith(false)),
                this.rolesFormGroup.get(RoleEnum.ClinicalFeedback).valueChanges.pipe(startWith(false)),
                this.rolesFormGroup.get(RoleEnum.ImagingRequestViewer)?.valueChanges.pipe(startWith(false)) ?? of(false),
                this.rolesFormGroup.get(RoleEnum.ImagingRequestEditor)?.valueChanges.pipe(startWith(false)) ?? of(false),
                this.sitesSource$
            ]).subscribe(
                ([generalAccessToggle, clinicalFeedbackToggle, imagingRequestViewerToggle, imagingRequestEditorToggle, allSites]) => {
                    if (allSites.length === 1) {
                        if (generalAccessToggle || clinicalFeedbackToggle || imagingRequestViewerToggle || imagingRequestEditorToggle) {
                            this.permittedSites.next([allSites[0].siteId]);
                            this.defaultSiteFormControl.setValue(allSites[0].siteId);
                            this.defaultSiteFormControl.markAsDirty();
                        } else {
                            this.permittedSites.next([]);
                            this.defaultSiteFormControl.setValue(0);
                            this.defaultSiteFormControl.markAsDirty();
                        }
                    }
                }
            )
        );

        this.numSiteSelected$ = this.updatedSitePermission.asObservable().pipe(
            debounceTime(10),
            switchMap(items => of(this.selectedSites(items).length)),
            shareReplay()
        );
    }

    private loadUser() {
        const userDetail$ = this.userSettingsService.getUsersByGuidV2(this.id);

        this.subscriptions.add(
            userDetail$.pipe(map(user => user.mfaEnabled ?? false)).subscribe(userHasMfa => this.userHasMfa$.next(userHasMfa))
        );

        this.subscriptions.add(
            userDetail$
                .pipe(
                    distinctUntilChanged(),
                    switchMap(user => {
                        const userOrgs = user.userOrgs;
                        if (userOrgs.length > 0) {
                            const userOrg = userOrgs.find(userOrg => userOrg.orgCode === this.orgSelected.value.orgCode);
                            return of(
                                userOrg.roles
                                    ?.filter(role =>
                                        [
                                            RoleEnum.GeneralAccess,
                                            RoleEnum.ClinicalFeedback,
                                            RoleEnum.ImagingRequestViewer,
                                            RoleEnum.ImagingRequestEditor
                                        ].includes(role.roleType)
                                    )
                                    .map(role => role.siteId)
                                    .filter(this.distinct) || ([] as number[])
                            );
                        } else {
                            return of([] as number[]);
                        }
                    })
                )
                .subscribe(value => {
                    this.permittedSites.next(value);
                })
        );

        const incomingDefaultSite$ = userDetail$.pipe(
            distinctUntilChanged(),
            switchMap(user => {
                const userOrgs = user.userOrgs;
                if (userOrgs.length > 0) {
                    const userOrg = userOrgs.find(userOrg => userOrg.orgCode == this.orgSelected.value.orgCode);
                    return of(userOrg.defaultSiteId);
                } else {
                    return of(0);
                }
            })
        );
        this.currentDefaultSite$ = combineLatest([
            incomingDefaultSite$,
            this.defaultSiteFormControl.valueChanges.pipe(startWith(null))
        ]).pipe(
            switchMap(([incomingDefaultSite, formDefaultSite]) => {
                if (incomingDefaultSite !== formDefaultSite) {
                    return of(formDefaultSite !== null ? formDefaultSite : incomingDefaultSite);
                } else {
                    return of(incomingDefaultSite);
                }
            })
        );

        this.subscriptions.add(
            combineLatest([this.defaultCountryCode$, userDetail$])
                .pipe(catchError(this.getErrorMessage))
                .subscribe({
                    next: ([defaultCountryCode, user]) => {
                        this.viewingUserBehaviorSubject.next(user);
                        if (user.phoneNumberCountryCode) {
                            this.countryCode = user.phoneNumberCountryCode;
                            this.countryCode$.next(this.countryCode);
                        } else {
                            this.countryCode = defaultCountryCode;
                            this.countryCode$.next(defaultCountryCode);
                        }

                        this.userDetailsForm.title.setValue(user.title);
                        this.userDetailsForm.firstName.setValue(user.firstName);
                        this.userDetailsForm.lastName.setValue(user.surname);
                        this.userDetailsForm.emailAddress.setValue(user.emailAddress);
                        this.userDetailsForm.contactPhone.setValue(user.phoneNumber);
                        this.userDetailsForm.contactPhoneExt.setValue(user.phoneNumberExtension);
                        const userOrg = user.userOrgs.find(userOrg => userOrg.orgCode === this.orgSelected.value.orgCode);
                        if (userOrg) {
                            this.defaultSiteFormControl.reset(userOrg.defaultSiteId ?? 0, {
                                onlySelf: true,
                                emitEvent: false
                            });

                            this.roles.forEach(role => {
                                if (
                                    userOrg.roles?.some(curRole => curRole.roleType === RoleMappingService.getRoleEnum(role.name)) ??
                                    false
                                ) {
                                    this.rolesFormGroup.get(RoleMappingService.getRoleEnum(role.name)).patchValue(true);
                                }
                            });
                        }

                        this.spinnerService.hide();
                        this.loading = false;
                    },
                    error: error => {
                        this.snackbarService.openSnackBar(error, SnackbarService.SNACK_BAR_CLASS_ALERT);
                        this.spinnerService.hide();
                    }
                })
        );
    }

    private distinct(value: number | null, index: number, self: (number | null)[]): boolean {
        return self.indexOf(value) === index;
    }

    private onPhoneNumberChanged(contactNumber: string, countryCode: string) {
        if (!contactNumber) {
            this.userDetailsForm.contactPhoneExt.reset();
            return;
        }
        const phoneNumberNew = countryCode + ' ' + contactNumber;

        let validNumber = false;
        let phoneNumber: PhoneNumber;
        try {
            phoneNumber = phoneUtil.parseAndKeepRawInput(phoneNumberNew, '');
            validNumber = phoneUtil.isValidNumber(phoneNumber);
        } catch (e) {}

        if (validNumber) {
            this.userDetailsForm.contactPhone.setValue(phoneNumber.getNationalNumber().toString(), { emitEvent: false });
        } else {
            this.userDetailsForm.contactPhone.setErrors({ invalidphone: true }, { emitEvent: false });
        }
    }

    private markAsTouched(form: UntypedFormGroup) {
        form.markAllAsTouched();
    }

    private getUserDetailsFromForm(): PatchUser {
        const hasPhoneNumber = !!this.userDetailsForm.contactPhone.value;
        return new PatchUser({
            title: this.userDetailsForm.title.value,
            firstName: this.userDetailsForm.firstName.value,
            surname: this.userDetailsForm.lastName.value,
            phoneNumberCountryCode: hasPhoneNumber ? this.countryCode : null,
            phoneNumber: this.userDetailsForm.contactPhone.value,
            phoneNumberExtension: hasPhoneNumber ? this.userDetailsForm.contactPhoneExt.value : null
        });
    }

    private getUserRolesFromForm(): UpdateUserRolesRequest {
        const result: UpdateUserOrgRole[] = [];
        Object.keys(this.rolesForm).forEach((key: RoleEnum) => {
            switch (key) {
                case RoleEnum.Admin:
                    if (this.rolesFormGroup.get(RoleEnum.Admin).value) {
                        result.push(
                            new UpdateUserOrgRole({
                                roleType: RoleEnum.Admin,
                                siteId: null
                            })
                        );
                    }
                    break;

                case RoleEnum.GeneralAccess:
                case RoleEnum.ClinicalFeedback:
                case RoleEnum.ImagingRequestViewer:
                case RoleEnum.ImagingRequestEditor:
                    this.setSelectedSites(result, key);
                    break;

                default:
                    if (this.rolesFormGroup.get(key).value) {
                        result.push(
                            new UpdateUserOrgRole({
                                roleType: key,
                                siteId: null
                            })
                        );
                    }
                    break;
            }
        });

        return new UpdateUserRolesRequest({
            roles: result
        });
    }

    private setSelectedSites(userDetails: UpdateUserOrgRole[], role: RoleEnum) {
        if (this.rolesFormGroup.get(role).value && this.selectedSites(this.updatedSitePermission.value).length > 0) {
            if (this.selectedSites(this.updatedSitePermission.value).length === this.updatedSitePermission.value?.length) {
                userDetails.push(
                    new UpdateUserOrgRole({
                        roleType: role,
                        siteId: null
                    })
                );
            } else {
                this.updatedSitePermission.value
                    .filter(info => info.isPermitted)
                    .forEach(info => {
                        userDetails.push(
                            new UpdateUserOrgRole({
                                roleType: role,
                                siteId: info.siteInfo.siteId
                            })
                        );
                    });
            }
        }
    }

    onSitePermissionChanged(sitePermissionList: SitePermissionInfo[]): void {
        this.updatedSitePermission.next(sitePermissionList);
        if (sitePermissionList.length === 1) {
            let nextDefaultSite;
            if (sitePermissionList[0].isPermitted) {
                nextDefaultSite = sitePermissionList[0].siteInfo.siteId;
            } else {
                nextDefaultSite = 0;
            }
            if (this.defaultSiteFormControl.value !== nextDefaultSite) {
                this.defaultSiteFormControl.markAsDirty();
            }
            this.defaultSiteFormControl.setValue(nextDefaultSite);
        } else if (!sitePermissionList.some(item => item.siteInfo.siteId === this.defaultSiteFormControl.value && item.isPermitted)) {
            if (this.defaultSiteFormControl.value !== 0) {
                this.defaultSiteFormControl.markAsDirty();
            }
            this.defaultSiteFormControl.setValue(0);
        }

        const existingSet = new Set(this.permittedSites.value);
        const selectedArr = this.selectedSites(sitePermissionList);
        this.isSitePermissionChanged = !(
            existingSet.size === selectedArr.length && selectedArr.every(item => existingSet.has(item.siteInfo.siteId))
        );
    }

    selectedSites(permissionList: SitePermissionInfo[]): SitePermissionInfo[] {
        return permissionList?.filter(item => item.isPermitted) ?? [];
    }

    isRoleAndSiteValid(): boolean {
        return !(
            this.rolesFormGroup.get(RoleEnum.GeneralAccess).value && this.selectedSites(this.updatedSitePermission.value).length === 0
        );
    }

    hasSiteSpecificRole(): boolean {
        return (
            this.rolesFormGroup.get(this.roleEnum.GeneralAccess).value ||
            this.rolesFormGroup.get(this.roleEnum.ClinicalFeedback).value ||
            this.rolesFormGroup.get(this.roleEnum.ImagingRequestViewer)?.value === true ||
            this.rolesFormGroup.get(this.roleEnum.ImagingRequestEditor)?.value === true
        );
    }

    disableAdminRole(role: Role) {
        return this.loggedInUser.id === this.id && role.name === 'Admin';
    }

    resetMfa() {
        if (!this.mfaResetting$.value) {
            this.mfaResetting$.next(true);
            this.subscriptions.add(
                this.userSettingsService
                    .resetUserMfa(this.id)
                    .pipe(
                        finalize(() => {
                            this.mfaResetting$.next(false);
                        })
                    )
                    .subscribe({
                        next: () => {
                            this.snackbarService.openSnackBar(this.UserMfaResetMessage, SnackbarService.SNACK_BAR_CLASS_SUCCESS);
                        },
                        error: () => {
                            this.snackbarService.openSnackBar(this.UserMfaResetErrorMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
                        }
                    })
            );
        }
    }
}
